// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import {
  FedExPickUpAddress,
  FedExPickupDetails,
  FedExShipperDetails,
  FedExShippingDetails,
} from "./FedExTypes";
import useDataFetchApi, { DataFetchState } from "utils/useDataFetchApi";

import { useCallback } from "react";
import firebase from "firebase";

export interface FedExPickupRequestData {
  registrationToken: string;
  address: FedExPickUpAddress;
  timezoneOffsetMinutes: number;
}

export interface FedExSchedulePickupRequestData
  extends FedExShippingDetails,
    FedExShipperDetails,
    FedExPickupDetails {
  registrationToken: string;
}

export interface FedExValidateAddressRequest {
  registrationToken: string;
  address: FedExPickUpAddress;
}

type BooleanString = "true" | "false";
export interface FedExValidateAddressResponse {
  normalizedAddress: FedExPickUpAddress;
  addressResolved: true;
  attributes: {
    CountrySupported: BooleanString;
    ZIP11Match: BooleanString;
  };
}

function getURLForFirebaseHttpsCallable(name: string): string {
  const region = "us-central1";
  const projectId = firebase.app().options["projectId"];
  return `https://${region}-${projectId}.cloudfunctions.net/${name}`;
}

export default (): [
  DataFetchState,
  {
    fetchFedExPickUpOptions: (data: FedExPickupRequestData) => void;
    scheduleFedexPickup: (data: FedExSchedulePickupRequestData) => void;
    validateAddress: (data: FedExValidateAddressRequest) => void;
  }
] => {
  const [state, setRequestConfig] = useDataFetchApi();

  const fetchFedExPickUpOptions = useCallback(
    (data: FedExPickupRequestData) => {
      setRequestConfig({
        url: getURLForFirebaseHttpsCallable("getFedexPickupOptions"),
        method: "post",
        data: { data },
      });
    },
    [setRequestConfig]
  );

  const scheduleFedexPickup = useCallback(
    (data: FedExSchedulePickupRequestData) => {
      setRequestConfig({
        url: getURLForFirebaseHttpsCallable("scheduleFedexPickup"),
        method: "post",
        data: { data },
      });
    },
    [setRequestConfig]
  );

  const validateAddress = useCallback(
    (data: FedExValidateAddressRequest) => {
      setRequestConfig({
        url: getURLForFirebaseHttpsCallable("fedexValidateAddress"),
        method: "post",
        data: { data },
      });
    },
    [setRequestConfig]
  );

  return [
    state,
    { fetchFedExPickUpOptions, scheduleFedexPickup, validateAddress },
  ];
};
